import React, { useState, useContext } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogPrimaryShortcut,
} from './ui/dialog';
import { Button } from './ui/button';
import { CancelButton } from './ui/cancel-button';
import { API_BASE } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { LocaleContext } from '../contexts/LocaleContext';

interface PlanChangeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentTier: 'free' | 'pro' | 'editorial' | undefined;
  targetTier: 'free' | 'pro' | 'editorial';
  onPlanChanged?: () => void;
}

export const PlanChangeModal: React.FC<PlanChangeModalProps> = ({
  open,
  onOpenChange,
  currentTier,
  targetTier,
  onPlanChanged,
}) => {
  const { token } = useAuth();
  const localeContext = useContext(LocaleContext);
  const t = localeContext?.t || ((key: string) => key);
  const [isChanging, setIsChanging] = useState(false);

  const isDowngrade = targetTier === 'free' && (currentTier === 'pro' || currentTier === 'editorial');

  const getMessage = (): string => {
    if (isDowngrade) {
      return t('downgrade_confirmation_message');
    }
    return t('plan_change_confirmation_message');
  };

  const getTitle = (): string => {
    if (isDowngrade) {
      return t('downgrade_plan_title');
    }
    return t('change_plan_title');
  };

  const handleAccept = async (): Promise<void> => {
    if (!token) {
      return;
    }

    try {
      setIsChanging(true);

      await fetch(`${API_BASE}/api/observability-events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          eventType: 'user_clicked_plan',
          userMessage: `User confirmed plan change from ${currentTier || 'unknown'} to ${targetTier}`,
        }),
      });

      if (isDowngrade) {
        const cancelResponse = await fetch(`${API_BASE}/api/stripe/cancel-subscription`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            immediately: false,
          }),
        });

        if (!cancelResponse.ok) {
          const error = await cancelResponse.json();
          if (error.error !== 'No active subscription') {
            throw new Error(error.error || 'Failed to cancel subscription');
          }
        }
      } else {
        const response = await fetch(`${API_BASE}/api/settings/change-plan`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            tier: targetTier,
          }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to change plan');
        }
      }

      onOpenChange(false);
      if (onPlanChanged) {
        onPlanChanged();
      }
      window.location.reload();
    } catch (error) {
      console.error('Error changing plan:', error);
      alert(t('error_changing_plan'));
    } finally {
      setIsChanging(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
          <DialogDescription>
            {getMessage()}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <CancelButton
            onClick={() => onOpenChange(false)}
            disabled={isChanging}
          >
            {t('cancel')}
          </CancelButton>
          <Button
            dialogPrimary
            onClick={handleAccept}
            disabled={isChanging}
            variant={isDowngrade ? 'destructive' : 'default'}
            className="inline-flex items-center gap-1.5"
          >
            {isChanging ? t('processing') : (isDowngrade ? t('confirm_downgrade') : t('accept'))}
            {!isChanging && <DialogPrimaryShortcut className="ml-0.5" />}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
